import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { getUsers, toggleProMembership, addRole, removeRole, searchUsers } from '@/data/adminUsers';
import { SearchUsersParams } from '@/contracts/adminUsers';
import { toast } from 'sonner';

export function useAdminUsers() {
  return useQuery({
    queryKey: ['admin-users'],
    queryFn: getUsers,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}

export function useSearchUsers(params: SearchUsersParams) {
  const { search, filterPro, filterRole, page = 1, pageSize = 25 } = params;

  return useQuery({
    queryKey: ['admin-users-search', { search, filterPro, filterRole, page, pageSize }],
    queryFn: () => searchUsers({ search, filterPro, filterRole, page, pageSize }),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}

export function useToggleProMembership() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, currentStatus }: { userId: string; currentStatus: boolean }) =>
      toggleProMembership(userId, currentStatus),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success('Pro membership updated');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update membership: ${error.message}`);
    },
  });
}

export function useToggleRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      role,
      hasRole,
    }: {
      userId: string;
      role: 'admin' | 'editor';
      hasRole: boolean;
    }) => (hasRole ? removeRole(userId, role) : addRole(userId, role)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success('Role updated');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update role: ${error.message}`);
    },
  });
}
